
/* injects from baggage-loader */
require('./style.styl');

import React, {Fragment} from 'react'
import PropTypes from 'prop-types'
import Icon from 'components/Icon'
import Button from 'components/Button'
import Checkbox from 'components/Checkbox'
import {CSSTransitionGroup} from 'react-transition-group'
import partition from 'lodash/partition'
import groupBy from 'lodash/groupBy'
import classnames from 'classnames'

class SiteSelector extends React.Component {
  static propTypes = {
    sites: PropTypes.arrayOf(PropTypes.shape({
      domain: PropTypes.string,
      id: PropTypes.string
    })),
    selectedSites: PropTypes.array,
    onSelectSite: PropTypes.func,
    onSelectMultiple: PropTypes.func,
    crossSiteMode: PropTypes.bool
  }

  static defaultProps = {
    sites: [],
    selectedSites: []
  }

  constructor(props) {
    super(props)

    const multipleMode = props.crossSiteMode || false
    const selectedSites = props.selectedSites

    const [selected, rest] = multipleMode
      ? partition(props.sites, ({id}) => selectedSites.includes(id))
      : [props.sites, []]

    this.state = {
      searchValue: '',
      multipleMode,
      selectedSites,
      sites: selected.concat(rest)
    }
  }

  render() {
    return (
      <div className='site-selector'>
        {this.renderMultiple()}
        {this.renderSearch()}
        {this.renderSites()}
      </div>
    )
  }

  renderMultiple() {
    const {multipleMode, selectedSites, searchValue} = this.state

    if (!multipleMode) {
      return (
        <div className='site-selector_multiple is-single_mode' onClick={() => { this.setState({multipleMode: true}) }}>
          Select Multiple Accounts
        </div>
      )
    }

    return (
      <div className='site-selector_multiple'>
        <div className='site-selector_multiple_counter'>{this.renderMultipleCounter()}</div>
        <div className='site-selector_multiple_controls'>
          <div
            className='site-selector_multiple_control site-selector_multiple_control_cancel'
            onClick={evt => {
              setTimeout(() => this.setState({multipleMode: false}), 0)
            }}
          >
            Cancel
          </div>
          <div
            className='site-selector_multiple_control site-selector_multiple_control_all'
            onClick={() => {
              const {selectedSites} = this.state
              const newSelection = this.getFilteredSites().map(s => s.id)
              this.setState({
                selectedSites: [...new Set(selectedSites.concat(newSelection))],
                searchValue: ''
              })
            }}
          >
            {searchValue ? (!selectedSites.length ? 'Select Found' : 'Add Found') : 'Select All'}
          </div>
          <span>{`|`}</span>
          <div
            className='site-selector_multiple_control site-selector_multiple_control_none'
            onClick={() => { this.setState({selectedSites: []}) }}
          >
            None
          </div>
          <Button
            className='site-selector_multiple_button'
            color='blue'
            disabled={selectedSites.length < 2}
            onClick={this.onShowSelected}
          >
            Show
          </Button>
        </div>
      </div>
    )
  }

  renderMultipleCounter() {
    const {selectedSites} = this.state

    if (!selectedSites || !selectedSites.length) {
      return 'Nothing selected'
    }

    return (
      `${selectedSites.length} Selected`
    )
  }

  renderSearch() {
    const {searchValue, sites} = this.state

    if (sites.length <= 5) return null

    const searchClassNames = classnames('site-selector_search_clear', {
      'is-visible': !!searchValue
    })

    return (
      <div className='site-selector_search'>
        <Icon name='zoom' className='site-selector_search_icon' size='16px' />
        <input
          className='site-selector_search_input'
          value={searchValue}
          onChange={this.onSearchChange}
          placeholder='Search site'
        />
        <Icon name='new-cross' className={searchClassNames} size='10px' onClick={this.onSearchClear} />
      </div>
    )
  }

  renderSites() {
    const filteredSites = this.getFilteredSites()

    if (!filteredSites.length) {
      return (
        <div className='site-selector_sites'>
          <div className='site-selector_site is-empty'>
            There's no sites match you query
          </div>
        </div>
      )
    }

    const groupedSites = Object.entries(groupBy(filteredSites, 'merchantName'))

    if (groupedSites.length === 1) {
      return (
        <div className='site-selector_sites'>
          {filteredSites.map(this.renderSite)}
        </div>
      )
    }

    return (
      <div className='site-selector_sites'>
        {groupedSites.map(([merchantId, sites], index) => (
          <Fragment key={index}>
            <div className='site-selector_site_merchant'>{merchantId}</div>
            {sites.map(this.renderSite)}
          </Fragment>
        ))}
      </div>
    )
  }

  renderSite = (site, index) => {
    const {multipleMode} = this.state
    const classNames = classnames('site-selector_site', {
      'with-checkbox': multipleMode
    })

    return (
      <div className={classNames} key={index} onClick={this.onSiteClick(site)}>
        <CSSTransitionGroup
          transitionName='is-transition'
          transitionEnterTimeout={200}
          transitionLeaveTimeout={200}
        >
          {this.renderCheckbox(site)}
        </CSSTransitionGroup>
        {site.name}
      </div>
    )
  }

  renderCheckbox(site) {
    const {selectedSites, multipleMode} = this.state
    const checked = selectedSites.includes(site.id)

    if (!multipleMode) return null

    return (
      <Checkbox
        checked={checked}
        className='site-selector_site_checkbox'
      />
    )
  }

  onSiteClick = site => evt => {
    const {multipleMode, selectedSites} = this.state
    const {onSelectSite} = this.props
    const checked = selectedSites.includes(site.id)

    if (!multipleMode) return onSelectSite(site.id)

    setTimeout(() => {
      if (checked) {
        this.setState({
          selectedSites: selectedSites.filter(id => id !== site.id)
        })
      } else {
        this.setState({
          selectedSites: selectedSites.concat(site.id)
        })
      }
    }, 0)
  }

  onShowSelected = evt => {
    const {multipleMode, selectedSites} = this.state
    const {onSelectMultiple} = this.props

    if (!multipleMode || !selectedSites.length) return false

    return onSelectMultiple(selectedSites)
  }

  onSearchChange = evt => {
    const value = evt.target.value

    this.setState({searchValue: value})
  }

  onSearchClear = evt => {
    this.setState({searchValue: ''})
  }

  getFilteredSites() {
    const {searchValue, sites} = this.state

    if (!searchValue) return sites

    return sites.filter(site => site.name.toLowerCase().includes(searchValue.toLowerCase()))
  }
}

export default SiteSelector



// WEBPACK FOOTER //
// ./client/components/SiteSelector/index.js